import React, { useState, useRef } from 'react';
import { toPng } from 'html-to-image';
import { toast } from 'sonner';
import { Trophy, ChevronsLeftRight, AlignLeft, Share2, Loader2 } from 'lucide-react';

/**
 * Single-elimination bracket with proper round alignment.
 *
 * Layout strategy:
 *  - Each round is a vertical flex column with the SAME total height.
 *  - Matches are distributed via `justify-around` so that round k+1 (with
 *    half the matches) lines up exactly between every pair of round k matches.
 *  - A pseudo-element `::after` on each non-final match draws a small
 *    horizontal connector toward the next round.
 *
 * Props:
 *  - matches: array of match objects with `round`, `bracket_position`,
 *             `home_club`, `away_club`, `home_score`, `away_score`, `status`
 *  - isItalian: boolean for i18n
 */

const ClubBadge = ({ club: rawClub, score, winner, isBye, expanded }) => {
  // Defensive top-level coercion: if upstream sent an ARRAY of clubs (bug
  // seen in production where home_club ended up being a list of 3 clubs
  // rendered overlapping in the same cell), grab the first valid entry.
  const club = Array.isArray(rawClub)
    ? (rawClub.find((c) => c && typeof c === 'object') || rawClub[0])
    : rawClub;
  if (!club) {
    return (
      <div className="flex items-center gap-2 px-2.5 py-2 text-slate-500 text-xs italic">
        <div className="w-5 h-5 rounded bg-slate-800/60 shrink-0" />
        <span className="truncate flex-1">— da definire —</span>
        <span className="font-mono text-slate-600 w-5 text-right">-</span>
      </div>
    );
  }
  // Defensive: coerce name to a single string even if the backend ships
  // an array or object (prevents the "Gam Aprodlitiollito" rendering bug
  // where multiple names overlap visually).
  const rawName = club?.name;
  const safeName = Array.isArray(rawName)
    ? rawName[0]
    : (typeof rawName === 'string' ? rawName : String(rawName ?? '?'));
  const bg = club.colors?.primary || '#1a1a2e';
  return (
    <div
      className={`flex items-center gap-2 px-2.5 py-2 text-xs sm:text-sm transition-colors ${
        winner ? 'bg-[#00E676]/15' : ''
      }`}
      title={safeName}
    >
      {club.logo ? (
        <img
          src={club.logo}
          alt={safeName}
          className="w-5 h-5 rounded object-cover shrink-0"
          loading="lazy"
        />
      ) : (
        <div
          className="w-5 h-5 rounded flex items-center justify-center text-[8px] font-bold text-white shrink-0"
          style={{ background: bg }}
        >
          {safeName?.charAt(0) || '?'}
        </div>
      )}
      {/* `min-w-0` + `block` are critical here: Tailwind's `truncate` adds
          `white-space: nowrap` + `overflow: hidden` + `text-overflow: ellipsis`,
          but inside a flex container the default min-content of an inline
          `<span>` prevents shrinking — causing long club names like
          "SC DRAGON" to wrap to a new line (looks like "duplicated names").
          `min-w-0` lets the flex item shrink below its content width so the
          ellipsis can finally kick in. */}
      <span
        className={`min-w-0 flex-1 font-medium ${
          expanded
            ? 'whitespace-normal break-words leading-tight'
            : 'truncate block'
        } ${
          winner ? 'text-[#00E676]' : isBye ? 'text-slate-500 italic' : 'text-white'
        }`}
      >
        {safeName || '?'}
      </span>
      <span
        className={`font-mono w-5 shrink-0 text-right tabular-nums ${
          winner ? 'text-[#00E676] font-bold' : 'text-slate-300'
        }`}
      >
        {score ?? '-'}
      </span>
    </div>
  );
};

const BracketMatch = ({ match, hasConnector, expanded }) => {
  const completed = match.status === 'completed';
  const homeWin = completed && (match.home_score ?? 0) > (match.away_score ?? 0);
  const awayWin = completed && (match.away_score ?? 0) > (match.home_score ?? 0);
  const isBye = completed && (!match.home_club || !match.away_club);

  return (
    <div className="relative" data-testid={`bracket-match-${match.match_id}`}>
      <div
        className={`bg-[#0a0d15] border rounded overflow-hidden transition-colors ${
          expanded ? 'w-[220px] sm:w-[240px]' : 'w-[180px] sm:w-[220px]'
        } ${
          completed
            ? 'border-[#00E676]/30 hover:border-[#00E676]/50'
            : 'border-white/10 hover:border-[#00F0FF]/40'
        }`}
      >
        {completed && !isBye && (
          <div className="px-2.5 py-0.5 text-[9px] uppercase tracking-wider text-[#00E676] bg-[#00E676]/10 border-b border-[#00E676]/20 flex items-center justify-end gap-1">
            <span>●</span>
            <span>FT</span>
          </div>
        )}
        {isBye && (
          <div className="px-2.5 py-0.5 text-[9px] uppercase tracking-wider text-slate-500 bg-white/[0.02] border-b border-white/5 flex items-center justify-end">
            BYE
          </div>
        )}
        <ClubBadge club={match.home_club} score={match.home_score} winner={homeWin} isBye={isBye && !match.home_club} expanded={expanded} />
        <div className="border-t border-white/5" />
        <ClubBadge club={match.away_club} score={match.away_score} winner={awayWin} isBye={isBye && !match.away_club} expanded={expanded} />
      </div>
      {hasConnector && (
        <div
          aria-hidden
          className="hidden md:block absolute top-1/2 -right-6 w-6 h-px bg-white/20"
        />
      )}
    </div>
  );
};

export const KnockoutBracket = ({ matches, isItalian, shareTitle }) => {
  const [expanded, setExpanded] = useState(false);
  const [sharing, setSharing] = useState(false);
  const captureRef = useRef(null);

  const handleShare = async () => {
    if (!captureRef.current || sharing) return;
    setSharing(true);
    try {
      await new Promise((r) => setTimeout(r, 120));
      const node = captureRef.current;
      const dataUrl = await toPng(node, {
        backgroundColor: '#050508',
        pixelRatio: 2,
        width: node.scrollWidth,
        height: node.scrollHeight,
        style: { overflow: 'visible' },
      });
      const blob = await (await fetch(dataUrl)).blob();
      const fileName = `tabellone-${(shareTitle || 'torneo').toLowerCase().replace(/[^a-z0-9]+/g, '-').slice(0, 40)}.png`;
      const file = new File([blob], fileName, { type: 'image/png' });
      const shareData = {
        files: [file],
        title: shareTitle || (isItalian ? 'Tabellone Torneo' : 'Tournament Bracket'),
        text: isItalian
          ? `🏆 Tabellone di ${shareTitle || 'torneo'} — EPT Italy`
          : `🏆 ${shareTitle || 'Tournament'} bracket — EPT Italy`,
      };
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share(shareData);
      } else {
        const a = document.createElement('a');
        a.href = dataUrl;
        a.download = fileName;
        a.click();
        toast.success(isItalian ? 'Immagine del tabellone scaricata!' : 'Bracket image downloaded!');
      }
    } catch (err) {
      if (err?.name !== 'AbortError') {
        toast.error(isItalian ? 'Errore nella generazione dell\'immagine' : 'Failed to generate image');
      }
    } finally {
      setSharing(false);
    }
  };

  if (!matches || matches.length === 0) {
    return (
      <p className="text-center text-slate-500 py-8 text-sm">
        {isItalian
          ? 'Bracket non ancora generato. Verrà creato quando il torneo viene avviato.'
          : 'Bracket not generated yet.'}
      </p>
    );
  }

  // Group by round
  const rounds = {};
  matches.forEach((m) => {
    const r = m.round || 1;
    if (!rounds[r]) rounds[r] = [];
    rounds[r].push(m);
  });
  const roundNumbers = Object.keys(rounds).map(Number).sort((a, b) => a - b);

  // Compute the LOGICAL total rounds the bracket would have, based on the
  // number of matches in the first round (n/2 first-round matches → log2(n)
  // total rounds). This protects against missing later-round placeholders:
  // even if the Final was accidentally deleted, the label "Semifinal" stays
  // correct for round 1 rather than being demoted to "Final".
  const firstRoundMatches = (rounds[roundNumbers[0]] || []).length || 1;
  const totalRoundsByMatches = Math.max(1, Math.ceil(Math.log2(firstRoundMatches * 2)));
  const totalRoundsPresent = roundNumbers.length;
  const totalRounds = Math.max(totalRoundsByMatches, totalRoundsPresent);

  const labelForRound = (r) => {
    const fromEnd = totalRounds - r;
    if (!isItalian) {
      if (fromEnd === 0) return 'Final';
      if (fromEnd === 1) return 'Semifinal';
      if (fromEnd === 2) return 'Quarterfinal';
      if (fromEnd === 3) return 'Round of 16';
      if (fromEnd === 4) return 'Round of 32';
      return `Round ${r}`;
    }
    if (fromEnd === 0) return 'Finale';
    if (fromEnd === 1) return 'Semifinale';
    if (fromEnd === 2) return 'Quarti di finale';
    if (fromEnd === 3) return 'Ottavi di finale';
    if (fromEnd === 4) return 'Sedicesimi';
    return `Turno ${r}`;
  };

  // Find champion
  const finalMatches = rounds[roundNumbers[totalRounds - 1]] || [];
  const finalMatch = finalMatches[0];
  let champion = null;
  if (finalMatch && finalMatch.status === 'completed') {
    champion =
      (finalMatch.home_score ?? 0) > (finalMatch.away_score ?? 0)
        ? finalMatch.home_club
        : (finalMatch.away_score ?? 0) > (finalMatch.home_score ?? 0)
          ? finalMatch.away_club
          : null;
  }

  // Compute first-round match count to determine total bracket height.
  // Each match card is ~72px tall; we set a minimum row height accordingly.
  const firstRoundCount = (rounds[roundNumbers[0]] || []).length;
  const MATCH_H = expanded ? 112 : 88; // expanded names can wrap to 2 lines
  const MIN_GAP = 12; // initial gap between round-1 matches
  const totalHeight = Math.max(280, firstRoundCount * MATCH_H + (firstRoundCount - 1) * MIN_GAP);

  return (
    <div className="space-y-4">
      {/* Toolbar: share (all screens) + compact/expanded toggle (mobile only) */}
      <div className="flex items-center justify-end gap-2">
        <button
          className="flex items-center gap-1.5 px-2.5 py-1.5 bg-[#00F0FF]/10 border border-[#00F0FF]/30 text-[#00F0FF] rounded-lg text-[10px] uppercase tracking-wider font-bold hover:bg-[#00F0FF]/20 transition-colors disabled:opacity-50"
          onClick={handleShare}
          disabled={sharing}
          data-testid="bracket-share-btn"
        >
          {sharing ? <Loader2 size={12} className="animate-spin" /> : <Share2 size={12} />}
          {isItalian ? 'Condividi' : 'Share'}
        </button>
        <div className="inline-flex md:hidden items-center bg-[#0a0d15] border border-white/10 rounded-lg overflow-hidden">
          <button
            className={`flex items-center gap-1.5 px-2.5 py-1.5 text-[10px] uppercase tracking-wider font-bold transition-colors ${
              !expanded ? 'bg-[#00F0FF]/15 text-[#00F0FF]' : 'text-slate-400 hover:text-white'
            }`}
            onClick={() => setExpanded(false)}
            data-testid="bracket-view-compact-btn"
          >
            <ChevronsLeftRight size={12} />
            {isItalian ? 'Compatto' : 'Compact'}
          </button>
          <button
            className={`flex items-center gap-1.5 px-2.5 py-1.5 text-[10px] uppercase tracking-wider font-bold transition-colors border-l border-white/10 ${
              expanded ? 'bg-[#00F0FF]/15 text-[#00F0FF]' : 'text-slate-400 hover:text-white'
            }`}
            onClick={() => setExpanded(true)}
            data-testid="bracket-view-expanded-btn"
          >
            <AlignLeft size={12} />
            {isItalian ? 'Esteso' : 'Expanded'}
          </button>
        </div>
      </div>

      <div ref={captureRef} className={sharing ? 'space-y-4 p-4' : 'space-y-4'}>
      {/* Branding header rendered only inside the shared image */}
      {sharing && (
        <div className="flex items-center gap-3 pb-1">
          <div className="w-9 h-9 rounded-lg bg-[#FFD700]/15 border border-[#FFD700]/40 flex items-center justify-center">
            <Trophy size={18} className="text-[#FFD700]" />
          </div>
          <div>
            <p className="font-heading text-white text-sm uppercase tracking-widest">
              {shareTitle || (isItalian ? 'Tabellone Torneo' : 'Tournament Bracket')}
            </p>
            <p className="text-[10px] uppercase tracking-widest text-[#00F0FF] font-bold">EPT Italy</p>
          </div>
        </div>
      )}

      {champion && (
        <div className="flex items-center justify-center gap-3 p-4 bg-gradient-to-r from-[#FFD700]/15 via-[#FFEA00]/10 to-[#FFD700]/15 border border-[#FFD700]/30 rounded">
          <Trophy size={24} className="text-[#FFD700]" />
          <div>
            <p className="text-[10px] uppercase tracking-wider text-[#FFD700] font-bold">
              {isItalian ? 'Campione' : 'Champion'}
            </p>
            <p className="text-lg font-heading text-white">{champion.name}</p>
          </div>
        </div>
      )}

      <div className={sharing ? 'overflow-visible w-max pb-2' : 'overflow-x-auto overflow-y-hidden -mx-2 px-2 pb-2 ept-thin-scrollbar'}>
        <div className="flex gap-8 sm:gap-10 md:gap-12 items-stretch min-w-max">
          {roundNumbers.map((r, idx) => {
            const roundMatches = (rounds[r] || []).slice().sort(
              (a, b) => (a.bracket_position ?? 0) - (b.bracket_position ?? 0)
            );
            const isLast = idx === roundNumbers.length - 1;

            return (
              <div
                key={r}
                className="flex flex-col"
                style={{ minHeight: `${totalHeight}px` }}
                data-testid={`bracket-round-${r}`}
              >
                <p className="text-[10px] sm:text-[11px] uppercase tracking-widest text-[#00F0FF] font-bold text-center mb-3 px-2 py-1 bg-[#00F0FF]/5 border border-[#00F0FF]/20 rounded">
                  {labelForRound(r)}
                </p>
                <div className="flex-1 flex flex-col justify-around gap-2">
                  {roundMatches.map((m) => (
                    <BracketMatch key={m.match_id} match={m} hasConnector={!isLast} expanded={expanded} />
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
      </div>
    </div>
  );
};

export default KnockoutBracket;
